import { useApolloClient } from '@apollo/client';
import {
  Button,
  ButtonList,
  Icon,
  ISortPayload,
  ITableColumn,
  Position,
  SortDirection,
  Table,
  TextAlign,
  Theme,
  Toast,
} from '@busybusy/webapp-react-ui';
import { TableCellHeight } from '@busybusy/webapp-react-ui/dist/components/Table/types/types';
import { EquipmentSearch, EquipmentSearchFields, SearchType } from '__generated__/graphql';
import { MANAGE_EQUIPMENT_QUERY } from 'apollo/queries/equipment-queries';
import { ListIcon } from 'assets/icons';
import classNames from 'classnames';
import { EmptyState, FeatureUnavailable, MoreButton, Panel, PanelContent, TableLoader } from 'components';
import CreateEquipmentFormDialog from 'components/domain/equipment/CreateEquipmentDialog/CreateEquipmentDialog';
import CSVImportEquipmentContainer from 'components/domain/equipment/CSVImportEquipment/CSVImportEquipmentContainer';
import { convertEquipmentToCsvEquipment } from 'components/domain/equipment/CSVImportEquipment/utils/utils';
import RowSettingsDialog from 'components/foundation/dialogs/RowSettingsDialog/RowSettingsDialog';
import LegacyMainHeader from 'components/layout/LegacyMainHeader/LegacyMainHeader';
import DeleteConfirmationDialog from 'containers/photos/DeleteConfirmationDialog/DeleteConfirmationDialog';
import { useActiveMember, useApolloPaging, useOpenable, useOrganization, useQueryParams } from 'hooks';
import useEquipment from 'hooks/models/equipment/useEquipment';
import useEquipmentManagementSettingsUpdate from 'hooks/models/member-settings/useEquipmentManagementSettingsUpdate';
import useManagementSettingsUpdate from 'hooks/models/member-settings/useManagementSettingsUpdate';
import useMemberSettings from 'hooks/models/member/useMemberSettings';
import useOnMount from 'hooks/utils/useOnMount/useOnMount';
import _, { cloneDeep, compact, isNil, partition } from 'lodash';
import Papa from 'papaparse';
import * as React from 'react';
import { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { Navigate } from 'react-router-dom';
import ICursorable from 'types/Cursorable';
import IEquipment from 'types/Equipment';
import { IVisibleTableColumn } from 'types/TableColumn';
import ITableRowHeightSettings from 'types/TableRowHeightSettings';
import { Nullable } from 'types/util/Nullable';
import { mergeListsWithOverride } from 'utils/collectionUtils';
import { QueryParam } from 'utils/constants/queryParam';
import { isoTimeStampLocal } from 'utils/dateUtils';
import { getCurrentEquipmentCostHistory } from 'utils/equipmentUtils';
import { useFeatureFlags } from 'utils/features';
import { downloadText } from 'utils/fileUtils';
import { t } from 'utils/localize';
import LocalStore from 'utils/localStorageUtils';
import { typedObjectEntries } from 'utils/objectUtils';
import { columns, defaultVisibleColumns } from './equipment-table-columns';
import './ManageEquipment.scss';
import ManageEquipmentActionHeader, {
  ManageEquipmentViewType,
} from './ManageEquipmentActionHeader/ManageEquipmentActionHeader';
import ManageEquipmentDetailsDialog from './ManageEquipmentDetailsDialog/ManageEquipmentDetailsDialog';
import ManageEquipmentFilter from './ManageEquipmentFilter/ManageEquipmentFilter';
import ManageEquipmentMap from './ManageEquipmentMap/ManageEquipmentMap';

export interface IManageEquipmentProps {
  className?: string | object;
}

export interface IManageEquipmentRowInfo {
  updatedByMember: any;
  id: string;
  name: string;
  make: string;
  model: string;
  year: string;
  category: string;
  hourMeter: string;
  costHistory: string;
  type: string;
  equipment: IEquipment;
  cursor: string;
  createdOn: string;
  updatedOn: Nullable<string>;
}

const ManageEquipment = (props: IManageEquipmentProps) => {
  const { className } = props;
  const memberSettings = useMemberSettings();
  const updateEquipmentManagementSettings = useEquipmentManagementSettingsUpdate();
  const updateManagementLists = useManagementSettingsUpdate();

  const activeMember = useActiveMember();
  const org = useOrganization();
  const isFeatureEnabled = useFeatureFlags('MANAGEMENT_LISTS', 'MANAGEMENT_LISTS_EQUIPMENT');
  const { getParam } = useQueryParams();
  const viewType: ManageEquipmentViewType = getParam('viewType') ?? 'list';
  const filterEquipmentId = getParam(QueryParam.EQUIPMENT_ID);
  const filterCategoryId = getParam(QueryParam.EQUIPMENT_CATEGORY_ID);
  const filterSearch = getParam(QueryParam.SEARCH);
  const filterPanelDetails = useOpenable({ isOpen: true });
  const deleteDetails = useOpenable();
  const equipmentDetails = useOpenable();
  const createEquipmentDetails = useOpenable();
  const rowSettingsDialog = useOpenable();
  const [errorToastMessage, setErrorToastMessage] = useState('');
  const client = useApolloClient();
  const [rowInfo, setRowInfo] = useState<IManageEquipmentRowInfo[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<IVisibleTableColumn[]>(getInitialVisibleColumns());
  const [loadedAll, setLoadedAll] = useState<boolean>(false);
  const [sortDir, setSortDir] = useState<SortDirection>(SortDirection.ASCENDING);
  const [sortBy, setSortBy] = useState<keyof IManageEquipmentRowInfo>('name');
  const [sortIsDirty, setSortIsDirty] = useState<boolean>(false);
  const defaultSectionSize = 15;
  const { getAll } = useApolloPaging();
  const [checkedRows, setCheckedRows] = useState<IManageEquipmentRowInfo[]>([]);
  const [checkOnLoad, setCheckOnLoad] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState<IEquipment | null>(null);
  const equipmentActions = useEquipment();
  const importDetails = useOpenable();
  const reimportDialogDetails = useOpenable();
  const isManagementListsSearchEnabled = useFeatureFlags('MANAGEMENT_LISTS_SEARCH');

  const [rowHeight, setLocalRowHeight] = useState(
    memberSettings?.web?.features?.managementLists?.rowHeight ?? 'standard'
  );
  const setRowHeight = (newSetting: TableCellHeight) => {
    updateManagementLists([{ key: 'rowHeight', payload: newSetting }]);
  };
  const useSmallIcon = rowHeight === 'cozy' || rowHeight === 'compact';

  useOnMount(() => {
    const migrateData = () => {
      const hasNotMigrated = isNil(memberSettings?.web?.features?.managementLists?.equipment?.localStoreMigrated);
      const localRowHeight: ITableRowHeightSettings = LocalStore.get('table-row-height-settings');
      const managementListRowHeight = localRowHeight?.managementLists;

      if (managementListRowHeight || hasNotMigrated) {
        const localColumnSettings = LocalStore.get('equipment_management_columns');
        updateManagementLists(
          compact([
            managementListRowHeight
              ? {
                  key: 'rowHeight',
                  payload: managementListRowHeight ?? memberSettings?.web?.features?.managementLists?.rowHeight,
                }
              : null,
            hasNotMigrated
              ? {
                  key: 'equipment',
                  payload: {
                    localStoreMigrated: true,
                    columnSettings: localColumnSettings,
                  },
                }
              : null,
          ])
        );
        if (localRowHeight) {
          setLocalRowHeight(managementListRowHeight);
          const isEveryKeyNull = typedObjectEntries(localRowHeight).every(
            ([key, val]) => key === 'managementLists' || isNil(val)
          );
          if (isEveryKeyNull) {
            LocalStore.remove('table-row-height-settings');
          } else {
            LocalStore.set('table-row-height-settings', { ...localRowHeight, managementLists: null });
          }
        }
      }
    };

    migrateData();
  });

  useEffect(() => {
    if (loadedAll && printing) {
      window.print();
      setPrinting(false);
    }
  }, [printing, loadedAll]);

  useEffect(() => {
    resetTable();
  }, [filterEquipmentId, filterCategoryId, filterSearch]);

  function getInitialVisibleColumns() {
    const savedColumns = memberSettings?.web?.features?.managementLists?.equipment?.columnSettings;
    const merged = _.sortBy(
      mergeListsWithOverride(defaultVisibleColumns, savedColumns ?? [], 'key', [
        'position',
        'visible',
      ]) as IVisibleTableColumn[],
      (item) => item.position ?? 0
    );
    return merged;
  }

  function handleVisibleColumnsChange(newVisibleColumns: IVisibleTableColumn[]) {
    updateEquipmentManagementSettings([{ key: 'columnSettings', payload: newVisibleColumns }]);
    setVisibleColumns(newVisibleColumns);
  }

  function getQueryVariables(after?: string) {
    let sort;
    const secondarySort =
      sortBy === 'name'
        ? { lastHours: { runningHours: sortDir.toLowerCase() } }
        : { equipmentName: sortDir.toLowerCase() };

    switch (sortBy) {
      case 'name':
        sort = [{ equipmentName: sortDir.toLowerCase() }, secondarySort];
        break;
      case 'make':
        sort = [
          { model: { make: { unknown: sortDir.toLowerCase() } } },
          { model: { make: { title: sortDir.toLowerCase() } } },
          secondarySort,
        ];
        break;
      case 'model':
        sort = [
          { model: { unknown: sortDir.toLowerCase() } },
          { model: { title: sortDir.toLowerCase() } },
          secondarySort,
        ];
        break;
      case 'year':
        sort = [{ model: { year: sortDir.toLowerCase() } }, secondarySort];
        break;
      case 'category':
        sort = [{ model: { category: { title: sortDir.toLowerCase() } } }, secondarySort];
        break;
      case 'hourMeter':
        sort = [{ lastHours: { runningHours: sortDir.toLowerCase() } }, secondarySort];
        break;
      case 'costHistory':
        sort = [secondarySort];
        break;
      case 'type':
        sort = [secondarySort];
        break;
      default:
        sort = [{ [sortBy]: sortDir.toLowerCase() }, secondarySort];
        break;
    }

    const variables = {
      filter: {
        deletedOn: { isNull: true },
      },
      first: defaultSectionSize,
      sort,
    } as any;

    if (filterEquipmentId) {
      variables.filter.id = { equal: filterEquipmentId };
    }
    if (filterCategoryId) {
      variables.filter.model = { category: { id: { equal: filterCategoryId } } };
    }
    if (after) {
      variables.after = after;
    }

    if (isManagementListsSearchEnabled) {
      variables.filter.search = getEquipmentSearch(filterSearch);
    }

    return variables;
  }

  async function fetchData(after?: string): Promise<IManageEquipmentRowInfo[]> {
    const results = await client.query<{ equipment: IEquipment[] }>({
      query: MANAGE_EQUIPMENT_QUERY,
      variables: getQueryVariables(after),
      fetchPolicy: 'network-only',
    });
    if (!_.isNil(results.errors)) {
      setErrorToastMessage(t('Server Error'));
      return [];
    }
    return createRowInfos(results.data.equipment);
  }

  function createRowInfos(equipment: IEquipment[]) {
    return equipment.map((e) => {
      const latestHistory = getCurrentEquipmentCostHistory(e.costHistory ?? []);
      return {
        id: e.id,
        name: e.equipmentName,
        make: !e.model.make.unknown ? e.model.make.title : '---',
        model: !e.model.unknown ? e.model.title : '---',
        year: e.model.year ? e.model.year.toString() : '---',
        category: e.model.category.title,
        hourMeter: e.trackManualHours ? (e.lastHours ? e.lastHours.runningHours.toString() : '---') : '---',
        costHistory: latestHistory ? (latestHistory.operatorCostRate ?? 0).toFixed(2) : '---',
        type: e.model.category.type ? e.model.category.type.title : '---',
        equipment: e,
        cursor: e.cursor,
        updatedOn: e.updatedOn,
        createdOn: e.createdOn,
        updatedByMember: e.updatedByMember,
      };
    });
  }

  function sortRows(rows: IManageEquipmentRowInfo[]): IManageEquipmentRowInfo[] {
    switch (sortBy) {
      case 'hourMeter': {
        // Always show empty hour meter values at end of list regardless of sort. Only applicable when sorting hourMeter column.
        const [hasHourMeterRows, noHourMeterRows] = partition(rows, (row) => row.hourMeter !== '---');
        return hasHourMeterRows.concat(noHourMeterRows);
      }
      case 'costHistory': {
        // Always show empty cost history values at end of list regardless of sort. Only applicable when sorting costHistory column.
        const [hasCostHistoryRows, noCostHistoryRows] = partition(rows, (row) => row.costHistory !== '---');
        return hasCostHistoryRows.concat(noCostHistoryRows);
      }
      case 'type': {
        const sortedRows = _.sortBy(rows, (r) => r.type.toLowerCase(), sortDir.toLowerCase());
        return sortDir.toLowerCase() === 'asc' ? sortedRows : sortedRows.reverse();
      }
      default:
        return rows;
    }
  }

  async function loadAll(
    onFinishLoading?: (rowInfo: IManageEquipmentRowInfo[], reimport: boolean) => void,
    reimport?: boolean
  ) {
    const lastCursor = _.last(rowInfo)?.equipment.cursor ?? '';
    const remainingEquipment = await getAll<IEquipment & ICursorable>('equipment', {
      query: MANAGE_EQUIPMENT_QUERY,
      variables: {
        ...getQueryVariables(lastCursor),
        first: 500,
      },
      fetchPolicy: 'network-only',
    });
    const allRows = [...rowInfo, ...createRowInfos(remainingEquipment)];
    setRowInfo(allRows);
    setLoadedAll(true);
    onFinishLoading?.(allRows, reimport ?? false);
  }

  function handleSort(sort: ISortPayload<IManageEquipmentRowInfo>) {
    setCheckedRows([]);
    setSortBy(sort.sortBy as any);
    setSortDir(sort.sortDir);
    setSortIsDirty(true);
    resetTable();
  }

  function resetTable() {
    setLoadedAll(false);
    setRowInfo([]);
  }

  function showManageEquipment() {
    return (activeMember.position?.manageEquipment ?? false) && org.trackEquipment ? org.trackEquipment : false;
  }

  function didLoad(newRows: IManageEquipmentRowInfo[], error: boolean, newLoadedAll: boolean) {
    setLoadedAll(newLoadedAll);
    if (!error) {
      const sortedRows = sortRows(newRows);
      setRowInfo(sortedRows);

      if (checkOnLoad) {
        setCheckedRows(sortedRows);

        if (newLoadedAll) {
          setCheckOnLoad(false);
        }
      }
    } else {
      setRowInfo([]);
      setCheckedRows([]);
    }
  }

  function renderEmptyState() {
    return (
      <EmptyState
        title={t('No Equipment')}
        subtitle={t('Click on the create button above to add a piece of equipment.')}
      />
    );
  }

  function filterColumns(cols: Array<ITableColumn<IManageEquipmentRowInfo>>) {
    const columnMap = _.keyBy(visibleColumns, 'key');
    const visibleCols = _.sortBy(
      cols.filter((column: ITableColumn<IManageEquipmentRowInfo>) => {
        const match = columnMap[column.key];
        return match && match.visible === true;
      }),
      (column, index) => columnMap[column.key].position ?? index
    );
    visibleCols.unshift(getActionColumn());

    return visibleCols;
  }

  function handleRowClick(row: IManageEquipmentRowInfo) {
    setSelectedEquipment(row.equipment);
    equipmentDetails.open();
  }

  function handleCheckAll(checked: boolean) {
    if (checked && !loadedAll) {
      loadAll(setCheckedRows);
    }
  }

  function handleSelectChange(newCheckedRows: IManageEquipmentRowInfo[]) {
    setCheckedRows(newCheckedRows);
  }

  function handleClearCheckedRows() {
    setCheckedRows([]);
  }

  function handlePrint() {
    if (loadedAll) {
      window.print();
    } else {
      setPrinting(true);
      loadAll();
    }
  }
  function handleCsvExport(reimport: boolean) {
    return () => {
      if (checkedRows.length > 0) {
        exportToCsv(checkedRows, false);
      } else if (loadedAll) {
        exportToCsv(rowInfo, reimport);
      } else {
        loadAll(exportToCsv, reimport);
      }
    };
  }

  function exportToCsv(allRows: IManageEquipmentRowInfo[], reimport: boolean) {
    if (allRows.length !== 0) {
      const csv = Papa.unparse(allRows.map((r) => r.equipment).map((e) => convertEquipmentToCsvEquipment(e, reimport)));
      handleClearCheckedRows();
      const title = reimport ? t('equipment-reimport') : t('equipment-export');
      downloadText(csv, `busybusy-${title}-${isoTimeStampLocal()}.csv`);
    } else {
      setErrorToastMessage(t('There is no data to export.'));
    }
  }

  function handleErrorToastClose() {
    setErrorToastMessage('');
  }

  function handleDelete() {
    if (selectedEquipment) {
      deleteEquipment([selectedEquipment]);
      setSelectedEquipment(null);
      if (equipmentDetails.isOpen) {
        equipmentDetails.close();
      }
    }
  }

  function handleGroupDelete(equipment: IEquipment[]) {
    deleteEquipment(equipment);
  }

  function deleteEquipment(equipment: IEquipment[]) {
    const ids = equipment.map((e) => e.id);
    ids.forEach((equipmentId) => {
      const info = _.find(rowInfo, { equipment: { id: equipmentId } });
      if (info) {
        _.remove(rowInfo, info);
      }
      equipmentActions.deleteEquipment(equipmentId);
    });
    setRowInfo(sortRows(_.sortBy(rowInfo, (item) => item.name.toLowerCase(), sortDir.toLowerCase())));
  }

  function handleCreateSubmit() {
    createEquipmentDetails.close();
    resetTable();
  }

  async function handleEdit(equipment: IEquipment) {
    const index = _.findLastIndex(rowInfo, { equipment: { id: equipment.id } });
    if (index !== -1) {
      const newInfo = cloneDeep(rowInfo);
      newInfo[index] = createRowInfos([equipment])[0];
      setRowInfo(newInfo);
    }
  }

  function handleEditClose() {
    equipmentDetails.close();
    setSelectedEquipment(null);
  }

  function getActionColumn() {
    return {
      align: TextAlign.CENTER,
      cell: (row: IManageEquipmentRowInfo, _col: ITableColumn<IManageEquipmentRowInfo>) => (
        <MoreButton
          className={`${useSmallIcon ? 'small-icon' : ''}`}
          position={Position.BOTTOM_START}
          renderContent={(close: () => void) => {
            return (
              <ButtonList>
                <Button
                  onClick={async (e: React.MouseEvent<Element, MouseEvent>) => {
                    e.preventDefault();
                    e.stopPropagation();
                    close();
                    setSelectedEquipment(row.equipment);
                    deleteDetails.open();
                  }}
                >
                  {t('Delete')}
                </Button>
              </ButtonList>
            );
          }}
        />
      ),
      cellClassName: 'px-1 no-print',
      headerClassName: 'no-print',
      key: 'actions',
      size: '60px',
      title: '',
      visibleByDefault: true,
    };
  }

  function renderList() {
    return (
      <TableLoader
        offset={50}
        className="pb-5 pl-1"
        data={rowInfo}
        loadedAll={loadedAll}
        getData={fetchData}
        didLoad={didLoad}
        sectionSize={defaultSectionSize}
        emptyTemplate={renderEmptyState()}
        flex={true}
        render={(data: IManageEquipmentRowInfo[], tableLoader?: HTMLElement) => {
          const filteredColumns = filterColumns(columns);
          return (
            <Table
              cols={filteredColumns}
              data={data}
              minWidth={filteredColumns.length * 220 + 'px'}
              lazyLoad={false}
              sortBy={sortBy}
              sortDir={sortDir}
              sortIsDirty={sortIsDirty}
              selected={checkedRows}
              onSelectChange={handleSelectChange}
              onCheckAllChange={handleCheckAll}
              onSort={handleSort}
              onRowClick={handleRowClick}
              strokeCols={false}
              scroller={tableLoader}
              cellHeight={rowHeight}
            />
          );
        }}
      />
    );
  }

  function renderMap() {
    return (
      <ManageEquipmentMap
        onDetailsClick={(e) => {
          setSelectedEquipment(e);
          equipmentDetails.open();
        }}
        onDirectionsClick={(e) => {
          window.open(
            `https://www.google.com/maps/search/?api=1&query=${e.lastLocation?.latitude},${e.lastLocation?.longitude}`,
            '_blank'
          );
        }}
        equipmentId={filterEquipmentId}
        categoryId={filterCategoryId}
        updatedEquipmentId={equipmentDetails.isOpen ? selectedEquipment?.id : undefined}
        search={filterSearch}
      />
    );
  }

  function renderMain() {
    return (
      <>
        {!showManageEquipment() && <Navigate to="/not-found" />}
        <Panel className={classes}>
          <LegacyMainHeader
            title={t('Equipment')}
            className="no-print"
            rightContent={
              <Button type="icon" onClick={filterPanelDetails.toggle}>
                <Icon svg={ListIcon} />
              </Button>
            }
          />
          <PanelContent>
            <Panel>
              <ManageEquipmentActionHeader
                checkedEquipment={checkedRows.map((r) => r.equipment)}
                clearCheckedEquipment={handleClearCheckedRows}
                openCreateDialog={createEquipmentDetails.open}
                onExportEquipment={handleCsvExport(false)}
                onImportEquipment={importDetails.open}
                onPrint={handlePrint}
                visibleColumns={visibleColumns}
                handleVisibleColumnsChange={handleVisibleColumnsChange}
                onDelete={handleGroupDelete}
                className="no-print"
                onEditUsingSpreadsheetClick={reimportDialogDetails.open}
                viewType={viewType}
                onRowHeightClick={rowSettingsDialog.open}
              />
              <h2 className="print mb-5">{t('Equipment')}</h2>
              <PanelContent>{viewType === 'list' ? renderList() : renderMap()}</PanelContent>
            </Panel>
            <Panel medium={true} strokeLeft={true} className="no-print" open={filterPanelDetails.isOpen}>
              <ManageEquipmentFilter />
            </Panel>
          </PanelContent>
          <Toast isOpen={errorToastMessage.length !== 0} onClose={handleErrorToastClose} theme={Theme.DANGER}>
            {errorToastMessage}
          </Toast>
        </Panel>
      </>
    );
  }

  const classes = classNames('manage-equipment', className);

  return (
    <>
      <Helmet>
        <title>{t('Manage Equipment')}</title>
      </Helmet>
      {isFeatureEnabled ? renderMain() : FeatureUnavailable}
      {selectedEquipment && (
        <DeleteConfirmationDialog
          isOpen={deleteDetails.isOpen}
          onClose={deleteDetails.close}
          onDelete={handleDelete}
          title={t('Delete equipment?')}
          showHeaderDivider={false}
        >
          {t('If you delete this item it will be gone forever. Are you sure you want to proceed?')}
        </DeleteConfirmationDialog>
      )}
      {selectedEquipment && (
        <ManageEquipmentDetailsDialog
          isOpen={equipmentDetails.isOpen}
          onClose={handleEditClose}
          equipment={selectedEquipment}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
      <CreateEquipmentFormDialog
        isOpen={createEquipmentDetails.isOpen}
        onClose={createEquipmentDetails.close}
        onSubmit={handleCreateSubmit}
        onImportClick={importDetails.open}
      />
      <CSVImportEquipmentContainer
        importDialogOpen={importDetails.isOpen}
        onImportDialogClose={importDetails.close}
        onImportComplete={resetTable}
        onReimportDialogClose={reimportDialogDetails.close}
        reimportDialogOpen={reimportDialogDetails.isOpen}
        onDownloadList={handleCsvExport(true)}
      />
      <RowSettingsDialog
        isOpen={rowSettingsDialog.isOpen}
        onClose={rowSettingsDialog.close}
        initialValue={rowHeight}
        onSubmit={setRowHeight}
      />
    </>
  );
};

export const getEquipmentSearch = (search: string | null): EquipmentSearch | undefined => {
  if (search && search.length) {
    return {
      type: SearchType.Contains,
      fields: [EquipmentSearchFields.EquipmentName, EquipmentSearchFields.MakeName, EquipmentSearchFields.ModelName],
      value: search,
    };
  } else {
    return undefined;
  }
};

export default ManageEquipment;
